import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type AdminInfo = {
  full_name: string | null;
  email: string;
};

type Message = { 
  id?: string;
  role: "user" | "assistant" | "support"; 
  content: string;
  admin_id?: string | null;
  created_at?: string;
  adminName?: string | null;
  adminEmail?: string | null;
  attachmentUrl?: string | null;
  attachmentType?: string | null;
};

type SupportRequest = {
  id: string;
  status: "pending" | "active" | "closed";
  created_at: string;
};

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;
const PUBLIC_SUPPORT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-support`;

// Generate or get guest session ID
const getGuestSessionId = (): string => {
  let sessionId = localStorage.getItem("guest_session_id");
  if (!sessionId) {
    sessionId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem("guest_session_id", sessionId);
  }
  return sessionId;
};

export const useSupportChat = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supportRequest, setSupportRequest] = useState<SupportRequest | null>(null);
  const [isSupportMode, setIsSupportMode] = useState(false);
  const [guestSessionId] = useState(getGuestSessionId);

  // Load existing messages and support request on mount
  useEffect(() => {
    const loadData = async () => {
      if (user) {
        // Authenticated user - load from database
        const { data: messagesData, error: messagesError } = await supabase
          .from("chat_messages")
          .select("id, message, role, admin_id, created_at, attachment_url, attachment_type")
          .eq("user_id", user.id)
          .order("created_at", { ascending: true });

        if (!messagesError && messagesData) {
          // Fetch admin profiles for messages with admin_id
          const adminIds = [...new Set(messagesData.filter(m => m.admin_id).map(m => m.admin_id))];
          let adminProfiles: Record<string, AdminInfo> = {};
          
          if (adminIds.length > 0) {
            const { data: profilesData } = await supabase
              .from("profiles")
              .select("user_id, full_name, email")
              .in("user_id", adminIds);
            
            if (profilesData) {
              adminProfiles = profilesData.reduce((acc, p) => {
                acc[p.user_id] = { full_name: p.full_name, email: p.email };
                return acc;
              }, {} as Record<string, AdminInfo>);
            }
          }

          setMessages(messagesData.map(m => {
            const adminInfo = m.admin_id ? adminProfiles[m.admin_id] : null;
            return { 
              id: m.id,
              role: m.admin_id ? "support" : m.role as "user" | "assistant", 
              content: m.message,
              admin_id: m.admin_id,
              created_at: m.created_at,
              adminName: adminInfo?.full_name || null,
              adminEmail: adminInfo?.email || null,
              attachmentUrl: m.attachment_url,
              attachmentType: m.attachment_type,
            };
          }));
        }

        // Check for active support request
        const { data: requestData } = await supabase
          .from("support_requests")
          .select("*")
          .eq("user_id", user.id)
          .neq("status", "closed")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (requestData) {
          setSupportRequest(requestData as SupportRequest);
          setIsSupportMode(true);
        }
      } else {
        // Guest user - load from localStorage
        const storedMessages = localStorage.getItem("guest_chat_messages");
        if (storedMessages) {
          try {
            setMessages(JSON.parse(storedMessages));
          } catch {
            // Ignore parse errors
          }
        }
        
        const storedSupport = localStorage.getItem("guest_support_mode");
        if (storedSupport === "true") {
          setIsSupportMode(true);
        }
      }
    };

    loadData();
  }, [user]);

  // Set up realtime subscription for new messages (authenticated users only)
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('chat-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          const newMsg = payload.new as any;
          // Only add if it's a support message (from admin)
          if (newMsg.admin_id) {
            // Fetch admin profile
            let adminName: string | null = null;
            let adminEmail: string | null = null;
            
            const { data: profileData } = await supabase
              .from("profiles")
              .select("full_name, email")
              .eq("user_id", newMsg.admin_id)
              .maybeSingle();
            
            if (profileData) {
              adminName = profileData.full_name;
              adminEmail = profileData.email;
            }

            setMessages((prev) => {
              // Check if already exists
              if (prev.some(m => m.id === newMsg.id)) return prev;
              return [...prev, {
                id: newMsg.id,
                role: "support" as const,
                content: newMsg.message,
                admin_id: newMsg.admin_id,
                created_at: newMsg.created_at,
                adminName,
                adminEmail,
                attachmentUrl: newMsg.attachment_url,
                attachmentType: newMsg.attachment_type,
              }];
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Poll for guest messages (every 5 seconds when in support mode)
  useEffect(() => {
    if (user || !isSupportMode) return;

    const pollForReplies = async () => {
      try {
        const response = await fetch(PUBLIC_SUPPORT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "get_messages",
            sessionId: guestSessionId,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.messages && data.messages.length > 0) {
            setMessages(data.messages.map((m: any) => ({
              id: m.id,
              role: m.admin_id ? "support" : m.role as "user" | "assistant",
              content: m.message,
              admin_id: m.admin_id,
              created_at: m.created_at,
              attachmentUrl: m.attachment_url,
              attachmentType: m.attachment_type,
            })));
            // Store in localStorage
            localStorage.setItem("guest_chat_messages", JSON.stringify(data.messages));
          }
        }
      } catch (err) {
        console.error("Poll error:", err);
      }
    };

    const interval = setInterval(pollForReplies, 5000);
    return () => clearInterval(interval);
  }, [user, isSupportMode, guestSessionId]);

  // Save message to database or localStorage
  const saveMessage = async (
    role: "user" | "assistant" | "support", 
    content: string,
    attachmentUrl?: string,
    attachmentType?: string
  ) => {
    if (user) {
      const { data } = await supabase.from("chat_messages").insert({
        user_id: user.id,
        role: role === "support" ? "assistant" : role,
        message: content,
        is_support_request: isSupportMode,
        attachment_url: attachmentUrl || null,
        attachment_type: attachmentType || null,
      }).select().single();
      return data;
    } else {
      // Guest - save to localStorage
      const newMessage: Message = {
        id: `local_${Date.now()}`,
        role,
        content,
        created_at: new Date().toISOString(),
        attachmentUrl,
        attachmentType,
      };
      const updatedMessages = [...messages, newMessage];
      localStorage.setItem("guest_chat_messages", JSON.stringify(updatedMessages));
      return newMessage;
    }
  };

  const requestSupport = useCallback(async () => {
    if (user) {
      // Authenticated user - create in database
      const { data, error } = await supabase
        .from("support_requests")
        .insert({
          user_id: user.id,
          status: "pending",
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating support request:", error);
        setError("فشل في إرسال طلب الدعم");
        return;
      }

      setSupportRequest(data as SupportRequest);

      // Notify admin via email
      try {
        await fetch(PUBLIC_SUPPORT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "notify_support",
            sessionId: `auth_${user.id}`,
            requestId: data.id,
            userEmail: user.email,
          }),
        });
      } catch (err) {
        console.error("Failed to notify admin:", err);
      }
    } else {
      // Guest user - create via public endpoint
      try {
        const response = await fetch(PUBLIC_SUPPORT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "create_request",
            sessionId: guestSessionId,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to create support request");
        }

        const data = await response.json();
        setSupportRequest({
          id: data.requestId,
          status: "pending",
          created_at: new Date().toISOString()
        });
        localStorage.setItem("guest_support_mode", "true");
      } catch (err) {
        console.error("Error creating guest support request:", err);
        setError("فشل في إرسال طلب الدعم");
        return;
      }
    }

    setIsSupportMode(true);

    // Add system message
    const systemMessage: Message = {
      role: "assistant",
      content: "سوف يتم طلب الدعم، يرجى الانتظار وسيتواصل معك فريق الدعم قريباً...",
    };
    setMessages((prev) => [...prev, systemMessage]);
    await saveMessage("assistant", systemMessage.content);
  }, [user, guestSessionId]);

  const cancelSupport = useCallback(async () => {
    if (user && supportRequest) {
      await supabase
        .from("support_requests")
        .update({ status: "closed", closed_at: new Date().toISOString() })
        .eq("id", supportRequest.id);
    } else {
      // Guest - just clear local storage
      localStorage.removeItem("guest_support_mode");
    }

    setSupportRequest(null);
    setIsSupportMode(false);

    const systemMessage: Message = {
      role: "assistant",
      content: "تم إلغاء طلب الدعم. يمكنك متابعة المحادثة مع المساعد الذكي.",
    };
    setMessages((prev) => [...prev, systemMessage]);
    await saveMessage("assistant", systemMessage.content);
  }, [user, supportRequest]);

  const sendMessage = useCallback(async (
    input: string, 
    attachmentUrl?: string, 
    attachmentType?: string
  ) => {
    if ((!input.trim() && !attachmentUrl) || isLoading) return;

    const userMsg: Message = { 
      role: "user", 
      content: input,
      attachmentUrl,
      attachmentType,
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);
    setError(null);

    // Save user message
    await saveMessage("user", input, attachmentUrl, attachmentType);

    // If in support mode
    if (isSupportMode) {
      if (!user) {
        // Guest in support mode - send to public endpoint
        try {
          await fetch(PUBLIC_SUPPORT_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "send_message",
              sessionId: guestSessionId,
              message: input,
              attachmentUrl,
              attachmentType,
            }),
          });
        } catch (err) {
          console.error("Error sending guest message:", err);
        }
      } else {
        // Authenticated user in support mode - notify admin via email
        try {
          await fetch(PUBLIC_SUPPORT_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "notify_message",
              sessionId: `auth_${user.id}`,
              userEmail: user.email,
              messageContent: input,
              attachmentUrl,
              attachmentType,
            }),
          });
        } catch (err) {
          console.error("Error notifying admin:", err);
        }
      }
      setIsLoading(false);
      return;
    }

    // Normal AI chat flow
    let assistantSoFar = "";

    const upsertAssistant = (nextChunk: string) => {
      assistantSoFar += nextChunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) =>
            i === prev.length - 1 ? { ...m, content: assistantSoFar } : m
          );
        }
        return [...prev, { role: "assistant", content: assistantSoFar }];
      });
    };

    try {
      const currentLang = localStorage.getItem("i18nextLng") || "ar";
      let resp: Response;

      if (user) {
        // Authenticated user - use chat endpoint
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          headers.Authorization = `Bearer ${sessionData.session.access_token}`;
        }
        resp = await fetch(CHAT_URL, {
          method: "POST",
          headers,
          body: JSON.stringify({ messages: [...messages, userMsg], language: currentLang }),
        });
      } else {
        // Guest user - use public-support endpoint with ai_chat action
        resp = await fetch(PUBLIC_SUPPORT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "ai_chat",
            chatMessages: [...messages, userMsg],
            language: currentLang,
          }),
        });
      }

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.error || "فشل في الاتصال بالذكاء الاصطناعي");
      }

      if (!resp.body) throw new Error("لا يوجد استجابة");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as
              | string
              | undefined;
            if (content) upsertAssistant(content);
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Final flush
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split("\n")) {
          if (!raw) continue;
          if (raw.endsWith("\r")) raw = raw.slice(0, -1);
          if (raw.startsWith(":") || raw.trim() === "") continue;
          if (!raw.startsWith("data: ")) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as
              | string
              | undefined;
            if (content) upsertAssistant(content);
          } catch {
            /* ignore */
          }
        }
      }

      // Save assistant response
      if (assistantSoFar) {
        await saveMessage("assistant", assistantSoFar);
      }
    } catch (e) {
      console.error("Chat error:", e);
      setError(e instanceof Error ? e.message : "خطأ غير معروف");
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, user, isSupportMode, guestSessionId]);

  const clearChat = useCallback(async () => {
    if (user) {
      // Delete all messages for this user
      await supabase
        .from("chat_messages")
        .delete()
        .eq("user_id", user.id);

      // Close any open support requests
      if (supportRequest) {
        await supabase
          .from("support_requests")
          .update({ status: "closed", closed_at: new Date().toISOString() })
          .eq("id", supportRequest.id);
      }
    } else {
      // Guest - clear localStorage
      localStorage.removeItem("guest_chat_messages");
      localStorage.removeItem("guest_support_mode");
    }
    setMessages([]);
    setError(null);
    setSupportRequest(null);
    setIsSupportMode(false);
  }, [user, supportRequest]);

  return { 
    messages, 
    isLoading, 
    error, 
    sendMessage, 
    clearChat,
    isSupportMode,
    supportRequest,
    requestSupport,
    cancelSupport,
    isGuest: !user
  };
};
